import { useMemo, useState } from 'react';
import type { CpGpMatchedProduct } from '../../../../../../models/Coupang';
import { EditorToolsPanel } from './EditorToolsPanel';
import type { ImageEditorTool, ImageEditMode } from '../types';
import { SectionHeader } from '../shared/SectionHeader';
import { SECTION_TITLE_STYLE, SECTION_BORDER_STYLE } from '../shared/styles';

interface ImageSourcePanelProps {
  readonly matchedProduct: CpGpMatchedProduct;
  readonly editMode: ImageEditMode;
  readonly selectedTool: ImageEditorTool;
  readonly onToolChange: (tool: ImageEditorTool) => void;
  readonly verifiedImages?: string[];
  readonly productsBySku?: any[];
  readonly textSettings?: { color: string; fontSize: number };
  readonly onTextSettingsChange?: (settings: Partial<{ color: string; fontSize: number }>) => void;
}

export const ImageSourcePanel = ({
  matchedProduct,
  editMode,
  selectedTool,
  onToolChange,
  verifiedImages = [],
  productsBySku = [],
  textSettings,
  onTextSettingsChange,
}: ImageSourcePanelProps) => {
  const [draggedImage, setDraggedImage] = useState<string | null>(null);

  const coupangProduct = matchedProduct.cpDetailedProduct;

  const sourceImages = useMemo(() => {
    const heroImages = coupangProduct?.heroImageUrls || [];
    const detailedImages = coupangProduct?.detailedImageUrls || [];

    const productImageUrls: string[] = [];
    const learnMoreUrls: string[] = [];

    productsBySku.forEach((product) => {
      if (product.productImageUrls && Array.isArray(product.productImageUrls)) {
        productImageUrls.push(...product.productImageUrls);
      }
      if (product.learnMoreUrl && Array.isArray(product.learnMoreUrl)) {
        learnMoreUrls.push(...product.learnMoreUrl);
      }
    });

    if (editMode === 'thu') {
      const verifiedThumbnails = verifiedImages.filter((url) => {
        const filename = url.split('/').pop() || '';
        return filename.startsWith('thu_');
      });
      const allThumbnails = [...heroImages, ...verifiedThumbnails, ...productImageUrls];
      const uniqueThumbnails = allThumbnails.filter((url, idx, arr) => arr.indexOf(url) === idx);
      return {
        thumbnails: uniqueThumbnails,
      };
    } else {
      const verifiedPdpImages = verifiedImages.filter((url) => {
        const filename = url.split('/').pop() || '';
        return filename.startsWith('pdp_') && url.includes('/en/');
      });
      const allPdpImages = [...detailedImages, ...verifiedPdpImages, ...learnMoreUrls];
      const uniquePdpImages = allPdpImages.filter((url, idx, arr) => arr.indexOf(url) === idx);
      return {
        pdpImages: uniquePdpImages,
      };
    }
  }, [coupangProduct, verifiedImages, editMode, productsBySku]);

  const handleDragStart = (e: React.DragEvent, imageUrl: string) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('imageUrl', imageUrl);
    e.dataTransfer.setData('productKey', 'source-panel');
    setDraggedImage(imageUrl);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
  };

  return (
    <div
      style={{
        width: '320px',
        background: 'white',
        borderRight: '1px solid #dee2e6',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px' }}>
        {/* Editor Tools */}
        <div style={{ ...SECTION_BORDER_STYLE, margin: '10px 0px' }}>
          <div className="mb-3">
            <small className="text-muted text-uppercase" style={SECTION_TITLE_STYLE}>
              Editor Tools
            </small>
          </div>
          <EditorToolsPanel
            selectedTool={selectedTool}
            onToolChange={onToolChange}
            textSettings={textSettings}
            onTextSettingsChange={onTextSettingsChange}
          />
        </div>

        {/* Source Images */}
        <div className="mt-4">
          <div className="mb-3">
            <small className="text-muted text-uppercase" style={SECTION_TITLE_STYLE}>
              Source Images
            </small>
          </div>
        </div>

        {/* Thumbnail Images (THU mode) */}
        {editMode === 'thu' && 'thumbnails' in sourceImages && (
          <div className="mb-4">
            <SectionHeader title="Thumbnail Images" count={sourceImages.thumbnails.length} />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '8px',
              }}
            >
              {sourceImages.thumbnails.map((url, idx) => (
                <div key={`thumb-${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, url)}
                    onDragEnd={handleDragEnd}
                    style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'grab',
                      border: '2px solid #dee2e6',
                      opacity: draggedImage === url ? 0.5 : 1,
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.borderColor = '#0d6efd')}
                    onMouseOut={(e) => (e.currentTarget.style.borderColor = '#dee2e6')}
                  >
                    <img
                      src={url}
                      alt={`Thumbnail ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: '0.65rem',
                      color: '#6c757d',
                      wordBreak: 'break-all',
                      lineHeight: '1.2',
                      padding: '2px 4px',
                    }}
                    title={url}
                  >
                    {url}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PDP Images (PDP mode) */}
        {editMode === 'pdp' && 'pdpImages' in sourceImages && (
          <div className="mb-4">
            <SectionHeader title="PDP Images" count={sourceImages.pdpImages.length} />
            <div className="d-grid gap-2">
              {sourceImages.pdpImages.map((url, idx) => (
                <div key={`pdp-${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, url)}
                    onDragEnd={handleDragEnd}
                    style={{
                      borderRadius: '8px',
                      overflow: 'hidden',
                      cursor: 'grab',
                      border: '2px solid #dee2e6',
                      opacity: draggedImage === url ? 0.5 : 1,
                      transition: 'all 0.2s',
                      maxHeight: '1024px',
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.borderColor = '#0d6efd')}
                    onMouseOut={(e) => (e.currentTarget.style.borderColor = '#dee2e6')}
                  >
                    <img
                      src={url}
                      alt={`PDP ${idx + 1}`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '1024px',
                        objectFit: 'contain',
                        objectPosition: 'top',
                        display: 'block',
                      }}
                    />
                  </div>
                  <div
                    style={{
                      fontSize: '0.65rem',
                      color: '#6c757d',
                      wordBreak: 'break-all',
                      lineHeight: '1.2',
                      padding: '2px 4px',
                    }}
                    title={url}
                  >
                    {url}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
